import React, { FC, useEffect, useState } from "react";
import { Checkbox, Link, Notification, Text } from "@telia/teddy";
import _find from "lodash/find";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import moment from "moment";
import { connect, useDispatch, useSelector } from "react-redux";
import ProductUserController from "./ProductUserController";
import { Alert } from "../../../../components";
import { FORM_ID } from "../../../../../../constants";
import { MAX_AGE_UNG } from "../../../../../../constants/app";
import { Form, Modal, RootState } from "../../../../../../models";
import SubscriptionAgeNotOkLightAlert from "../../../../checkout/components/atoms/SubscriptionAgeNotOkLightAlert/SubscriptionAgeNotOkLightAlert";
import ContentfulModal from "../../../../checkout/components/molecules/ContentfulModal/ContentfulModal";
import { getActiveSubscription, isSubscriptionDraft } from "../../../../checkout/state/selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../../../../checkout/state/selectors/combinedSelectors";
import {
    getSubscriptionMaxAgeAllowed,
    getSubscriptionName,
    isProductUserMandatory as isProductUserMandatorySelector,
    isChildSubscription as isChildSubscriptionSelector,
    isJuniorSubscription as isJuniorSubscriptionSelector,
    isUngSubscription as isUngSubscriptionSelector,
} from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { FieldSet, FormRow, FormRowFormats, Input } from "../../../../forms";
import { getModals, isSecretNumberModal } from "../../../../modals/state/selectors";
import { RapidForm, actionTypes as RF_AT } from "../../../../rapidform";
import { VALIDATION_FIELD } from "../../../../rapidform/formValidation";
import { setValue as setPersonalDetailsValueAction } from "../../../state/actions";
import * as CUSTOMER_INFO_SELECTORS from "../../../state/selectors/customerInfoSelectors";
import {
    getCustomerLegalOwnerIndividual,
    getExistingSubscriptionForChange,
} from "../../../state/selectors/customerSelectors";
import { getFormById } from "../../../state/selectors/formSelectors";
import TEXT from "../../../text-consts";
import "./CustomerInfoForm.less";
import { isValidSubscriptionAge } from "../../../../checkout/utils";

interface StateProps {
    campaignId?: string;
    form: Form;
    isCampaignAllowed: boolean;
    isCampaignToggled: boolean;
    isEmailTakenForTeliaSky?: boolean;
    isEmailTakenForTeliaTrygg?: boolean;
    isNotFound: boolean;
    isOptingOutForTeliaXServices?: boolean;
    isProductUserAgeOk: boolean;
    isProductUserDifferentThanLegalOwner: boolean;
    isProductUserMandatory: boolean;
    isSubscriptionDraft: boolean;
    isBarnOrJuniorSubscription: boolean;
    isZipNeeded: boolean;
    maxAgeAllowed?: number;
    secretNumberModal: Modal | undefined;
    subscriptionName: string;
}

interface CustomerInfoFormProps extends StateProps {
    onSubmitCustomerDetails: () => void;
}

const CustomerInfoForm: FC<CustomerInfoFormProps> = ({
    // Redux (data)
    maxAgeAllowed,
    isProductUserMandatory,
    secretNumberModal,
    form,
    campaignId,
    isCampaignToggled,
    isCampaignAllowed,
    isProductUserAgeOk,
    isProductUserDifferentThanLegalOwner,
    isEmailTakenForTeliaTrygg,
    isEmailTakenForTeliaSky,
    isNotFound,
    isOptingOutForTeliaXServices,
    isZipNeeded,
    subscriptionName,
    isBarnOrJuniorSubscription,
    isSubscriptionDraft,
    // Own props
    onSubmitCustomerDetails,
}) => {
    const [openSecretNumberModal, setOpenSecretNumberModal] = useState(false);
    const dispatch = useDispatch();
    const legalOwnerEmail = useSelector((state: RootState) => getCustomerLegalOwnerIndividual(state)?.email);
    const [isProductUserWithoutEmail, setIsProductUserWithoutEmail] = useState(false);
    const productUserEmail = form.values?.user?.productUserEmail;

    useEffect(() => {
        if (isProductUserDifferentThanLegalOwner) {
            const firstNameInput = document.querySelector<HTMLInputElement>('input[name="user-firstName"]');
            firstNameInput?.focus();
        }
    }, [isProductUserDifferentThanLegalOwner]);

    useEffect(() => {
        if (!isProductUserDifferentThanLegalOwner && legalOwnerEmail && !productUserEmail) {
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_PRODUCT_USER),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.CUSTOMER_PRODUCT_USER,
                    inputName: `user-${VALIDATION_FIELD.PRODUCT_USER_EMAIL}`,
                    inputValue: legalOwnerEmail,
                    visible: true,
                },
            });
        }
    }, [isProductUserDifferentThanLegalOwner, legalOwnerEmail, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally omit productUserEmail to avoid re-prefill on user edits

    useEffect(() => {
        setIsProductUserWithoutEmail(!isSubscriptionDraft && !productUserEmail);
    }, [isSubscriptionDraft, productUserEmail]);

    const setPersonalDetailsValue = (field: string, value: boolean) => {
        dispatch(setPersonalDetailsValueAction(field, value));
    };

    const streetAddress = form.values?.user?.[VALIDATION_FIELD.STREET];
    const zipCode = form.values?.user?.[VALIDATION_FIELD.POSTCODE];
    const isPostCodeFilled = !_isEmpty(zipCode);

    return (
        <div className="customer-form">
            <RapidForm
                id={FORM_ID.CUSTOMER_PRODUCT_USER}
                action={"#"}
                errors={form.errors}
                values={form.values}
                visible={form.visible}
                started={form.started}
                onSubmit={onSubmitCustomerDetails}
            >
                <FieldSet name="user">
                    <div className="customer-form__checkbox" data-cy="customer-form-checkboxes">
                        {!isProductUserMandatory && (
                            <ProductUserController
                                checked={!isProductUserDifferentThanLegalOwner}
                                setValue={(value) =>
                                    setPersonalDetailsValue("isProductUserDifferentThanLegalOwner", value)
                                }
                                disableLegal={isBarnOrJuniorSubscription}
                            />
                        )}
                    </div>
                    {isProductUserDifferentThanLegalOwner && (
                        <>
                            {/* TODO: Border: solid 1px #6E6E6E in Styleguide or override */}
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.FIRST_NAME}
                                label={TEXT.PERSONAL_DETAILS.FIRSTNAME}
                                data-di-mask
                            />
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.LAST_NAME}
                                label={TEXT.PERSONAL_DETAILS.LASTNAME}
                                data-di-mask
                            />
                            <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                <Input
                                    type="tel"
                                    name={VALIDATION_FIELD.BIRTHDATE}
                                    label={TEXT.PERSONAL_DETAILS.BIRTHDATE}
                                    placeholder="DD.MM.ÅÅÅÅ"
                                    data-di-mask
                                />
                                <div></div>
                            </FormRow>
                            {isZipNeeded && streetAddress && (
                                <Input
                                    type="text"
                                    name={VALIDATION_FIELD.STREET}
                                    label={TEXT.PERSONAL_DETAILS.STREET}
                                    data-di-mask
                                    disabled
                                />
                            )}
                            {isZipNeeded && (
                                <>
                                    {!isNotFound && isZipNeeded && (
                                        <Notification type="tile" variant="information" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Vi klarte ikke hente ut informasjon i folkeregisteret basert på
                                                fødselsdato og navn, og trenger derfor postnummer for å kunne
                                                identifisere riktig person. Postnummeret må samsvare med hva som er
                                                registrert i Folkeregisteret. Vennligst se over og prøv igjen.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                    {isNotFound && (
                                        <Notification type="tile" variant="warning" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Vi klarte ikke hente ut informasjon i folkeregisteret basert på
                                                fødselsdato, navn og postnummer. Vennligst sjekk at opplysningene er
                                                korrekte og samsvarer med hva som er registrert i{" "}
                                                <Link
                                                    href="https://www.skatteetaten.no/skjema/opplysninger-i-folkeregisteret/"
                                                    variant="text"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Folkeregisteret
                                                </Link>
                                                , og prøv igjen.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                    <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                        <Input
                                            type="text"
                                            name={VALIDATION_FIELD.POSTCODE}
                                            label={TEXT.PERSONAL_DETAILS.POSTCODE}
                                        />
                                        <Input
                                            disabled={isPostCodeFilled}
                                            type="text"
                                            name={VALIDATION_FIELD.CITY}
                                            label={TEXT.PERSONAL_DETAILS.CITY}
                                        />
                                    </FormRow>
                                </>
                            )}
                        </>
                    )}
                    {!isProductUserDifferentThanLegalOwner &&
                        !(isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                            <Input
                                type="email"
                                name={VALIDATION_FIELD.PRODUCT_USER_EMAIL}
                                label={TEXT.PERSONAL_DETAILS.EMAIL}
                                placeholder={isProductUserWithoutEmail ? "" : "ola@nordmann.no"}
                                disabled={isProductUserWithoutEmail}
                                data-di-mask
                            />
                        )}
                    {(isProductUserDifferentThanLegalOwner || isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                        <>
                            <Input
                                type="email"
                                name={VALIDATION_FIELD.PRODUCT_USER_EMAIL}
                                label={TEXT.PERSONAL_DETAILS.EMAIL}
                                placeholder={isProductUserWithoutEmail ? "" : "ola@nordmann.no"}
                                disabled={isProductUserWithoutEmail}
                                data-di-mask
                            />
                            {(isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                                <>
                                    <Notification type="tile" variant="warning" className="subscription-age-not-ok">
                                        <Notification.Icon />
                                        <Notification.Text>
                                            {TEXT.ADD_MEMBER.TELIA_X_SERVICE.UNIQUE_EMAIL(
                                                isEmailTakenForTeliaTrygg,
                                                isEmailTakenForTeliaSky,
                                                false,
                                            )}
                                        </Notification.Text>
                                    </Notification>
                                    <Checkbox>
                                        <Checkbox.Trigger
                                            checked={!!isOptingOutForTeliaXServices}
                                            onCheckedChange={() => {
                                                setPersonalDetailsValue(
                                                    "isOptingOutForTeliaXServices",
                                                    !isOptingOutForTeliaXServices,
                                                );
                                            }}
                                        />
                                        <span>
                                            {TEXT.PERSONAL_DETAILS.ACTIVATE_TELIA_TRYGG_LATER(
                                                isEmailTakenForTeliaTrygg,
                                                isEmailTakenForTeliaSky,
                                            )}
                                        </span>
                                    </Checkbox>
                                </>
                            )}
                        </>
                    )}
                    {isProductUserDifferentThanLegalOwner && (
                        <Checkbox className="customer-form__checkbox">
                            <Checkbox.Trigger
                                className="customer-form__checkbox__trigger"
                                checked={isProductUserWithoutEmail}
                                onCheckedChange={() => {
                                    const nextValue = !isProductUserWithoutEmail;
                                    setIsProductUserWithoutEmail(nextValue);

                                    if (nextValue) {
                                        dispatch({
                                            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_PRODUCT_USER),
                                            payload: {
                                                errorMessage: undefined,
                                                form: FORM_ID.CUSTOMER_PRODUCT_USER,
                                                inputName: `user-${VALIDATION_FIELD.PRODUCT_USER_EMAIL}`,
                                                inputValue: "",
                                                visible: true,
                                            },
                                        });
                                    }
                                }}
                            />
                            <span>Bruker har ikke e-post</span>
                        </Checkbox>
                    )}
                    {campaignId && (
                        <div className="customer-form__campaign-wrapper">
                            <img
                                width={120}
                                className="customer-form__img-campaign"
                                alt={TEXT.ADD_MEMBER.CAMPAIGN[campaignId].IMG_ALT}
                                src={TEXT.ADD_MEMBER.CAMPAIGN[campaignId].IMG_SRC}
                            />
                            <Text mb="100">{TEXT.ADD_MEMBER.CAMPAIGN[campaignId].DESCRIPTION(subscriptionName)}</Text>
                            {!isCampaignAllowed && (
                                <Alert className="subscription-age-not-ok" title="Obs!" kind="neutral" icon="alert">
                                    {TEXT.ADD_MEMBER.CAMPAIGN[campaignId].NOT_ALLOWED}
                                </Alert>
                            )}
                            {isCampaignAllowed && (
                                <Checkbox className="customer-form__checkbox">
                                    <Checkbox.Trigger
                                        className="customer-form__checkbox__trigger"
                                        checked={isCampaignToggled}
                                        onCheckedChange={() => {
                                            setPersonalDetailsValue("isCampaignToggled", !isCampaignToggled);
                                        }}
                                    />
                                    <span>{TEXT.ADD_MEMBER.CAMPAIGN[campaignId].OPTION}</span>
                                </Checkbox>
                            )}
                            <span className="customer-form__campaign-disclaimer">
                                {TEXT.ADD_MEMBER.CAMPAIGN[campaignId].DISCLAIMER}
                            </span>
                        </div>
                    )}
                </FieldSet>
                {!isProductUserAgeOk && <SubscriptionAgeNotOkLightAlert maxAgeAllowed={maxAgeAllowed} />}
            </RapidForm>
            {secretNumberModal && (
                <ContentfulModal
                    open={openSecretNumberModal}
                    setOpen={setOpenSecretNumberModal}
                    modal={secretNumberModal}
                />
            )}
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => {
    const selectedSubscriptionId = getActiveSubscription(state);
    const existingSubscriptionForChange = getExistingSubscriptionForChange(state);
    const form = getFormById(state, FORM_ID.CUSTOMER_PRODUCT_USER);
    const isProductUserDifferentThanLegalOwner = CUSTOMER_INFO_SELECTORS.isProductUserDifferentThanLegalOwner(state);
    const rawBirthDate = isProductUserDifferentThanLegalOwner
        ? _get(form, `values.user.${VALIDATION_FIELD.BIRTHDATE}`, undefined)
        : CUSTOMER_INFO_SELECTORS.getLegalOwnerBirthdate(state);
    const birthDate = _isEmpty(rawBirthDate) ? undefined : rawBirthDate;

    const subscriptionMaxAgeAllowed = getSubscriptionMaxAgeAllowed(state, selectedSubscriptionId);

    // For age-restricted subscriptions, always validate age even when legal owner = product user
    const isProductUserAgeOk = isValidSubscriptionAge({
        subscriptionMaxAgeAllowed,
        productUserBirthDate: birthDate,
        useProductUserAge: true,
    });

    return {
        campaignId: COMBINED_SELECTORS.getCampaignId(),
        subscriptionName: getSubscriptionName(state, selectedSubscriptionId),
        maxAgeAllowed: getSubscriptionMaxAgeAllowed(state, selectedSubscriptionId),
        isProductUserMandatory:
            isProductUserMandatorySelector(state, selectedSubscriptionId) || !!existingSubscriptionForChange,
        isBarnOrJuniorSubscription: (() => {
            if (!selectedSubscriptionId) return false;
            if (
                isChildSubscriptionSelector(state, selectedSubscriptionId) ||
                isJuniorSubscriptionSelector(state, selectedSubscriptionId)
            ) {
                return true;
            }
            if (isUngSubscriptionSelector(state, selectedSubscriptionId)) {
                const loBirthDate = getCustomerLegalOwnerIndividual(state)?.birthDate;
                if (!loBirthDate) return false;
                const loAge = moment().diff(moment(loBirthDate, ["DD.MM.YYYY", "YYYY-MM-DD"], true), "years", false);
                return loAge >= MAX_AGE_UNG;
            }
            return false;
        })(),
        isSubscriptionDraft: selectedSubscriptionId ? isSubscriptionDraft(state, selectedSubscriptionId) : false,
        secretNumberModal: _find(getModals(state), isSecretNumberModal),
        form,
        isCampaignToggled: CUSTOMER_INFO_SELECTORS.isCampaignToggled(state),
        isCampaignAllowed: CUSTOMER_INFO_SELECTORS.isCampaignAllowed(state),
        isProductUserAgeOk,
        isProductUserDifferentThanLegalOwner,
        isEmailTakenForTeliaSky: CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaSky(state),
        isEmailTakenForTeliaTrygg: CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaTrygg(state),
        isNotFound: CUSTOMER_INFO_SELECTORS.isNotFound(state),
        isOptingOutForTeliaXServices: CUSTOMER_INFO_SELECTORS.isOptingOutForTeliaXServices(state),
        isZipNeeded: CUSTOMER_INFO_SELECTORS.isZipNeeded(state),
        selectedSubscriptionId,
    };
};

export default connect(mapStateToProps)(CustomerInfoForm);
